// src/pages/SharedWithMe.js
// Component to display items shared with the current user, with fully consistent controls.

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Search, 
  Calendar, 
  User, 
  Copy, 
  Lock, 
  Eye,
  Clock,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X as XIcon
} from 'lucide-react';
import Navbar9 from '../components/Navbar9';
import { getSharedWithMe } from '../services/enterpriseSharing';
import CopySharedModal from '../components/CopySharedModal';

const ITEMS_PER_PAGE = 10; // You can change this to 2 for testing

const SharedWithMe = () => {
  const navigate = useNavigate();
  
  // --- State Management ---
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search State
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Sort State
  const [sortOption, setSortOption] = useState('dateDesc');
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const sortDropdownRef = useRef(null);

  // Modal State
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const sortOptions = {
    dateDesc: 'Date Shared (Newest)',
    dateAsc: 'Date Shared (Oldest)',
    titleAsc: 'Title (A-Z)',
    titleDesc: 'Title (Z-A)',
  };

  // --- Effects ---

  // Debounce search term to prevent API calls on every keystroke.
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // When a new search is performed, reset to the first page.
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]); // This is intentionally missing 'currentPage' to prevent resetting pagination on page change.

  // Main data fetching effect
  useEffect(() => {
    const loadSharedItems = async () => {
      try {
        setLoading(true);
        const response = await getSharedWithMe(currentPage, ITEMS_PER_PAGE, debouncedSearchTerm, sortOption);
        setAllItems(response.items || []);
        setTotalItems(response.totalItems || 0);
        setTotalPages(Math.ceil((response.totalItems || 0) / ITEMS_PER_PAGE));
      } catch (err) {
        console.error('Error loading shared items:', err);
        setError(err.message || 'Failed to load shared items');
      } finally {
        setLoading(false);
      }
    };
    loadSharedItems();
  }, [currentPage, debouncedSearchTerm, sortOption]);

  // Effect to handle closing the dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setIsSortDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [sortDropdownRef]);

  // --- Handlers ---

  const handleSortChange = (option) => {
    setSortOption(option);
    setCurrentPage(1);
    setIsSortDropdownOpen(false);
  };
  
  const handleCopySuccess = (result) => {
    setShowCopyModal(false);
    setSelectedItem(null);
    setAllItems(prevItems => 
      prevItems.map(item => 
        item.shareId === selectedItem.shareId 
          ? { ...item, hasCopied: true, lastCopiedAt: new Date().toISOString() }
          : item
      )
    );
    navigate('/project-details', { 
      state: { projectId: result.projectId }
    });
  };

  const handleViewItem = (item) => {
    const shareUrl = `${window.location.origin}/shared/${item.shareId}`;
    window.open(shareUrl, '_blank');
  };

  const handleCopyItem = (item) => {
    setSelectedItem(item);
    setShowCopyModal(true);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const getExpirationStatus = (expiresAt) => {
    if (!expiresAt) return null;
    const expireDate = new Date(expiresAt);
    const now = new Date();
    const diffTime = expireDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffTime <= 0) return { status: 'expired', text: 'Expired' };
    if (diffDays <= 1) return { status: 'expiring', text: 'Expires today' };
    if (diffDays <= 7) return { status: 'expiring', text: `Expires in ${diffDays} days` };
    return { status: 'active', text: `Expires ${expireDate.toLocaleDateString()}` };
  };

  // --- Render Logic ---

  if (loading && currentPage === 1 && !debouncedSearchTerm) {
    return (
      <div className="min-h-screen bg-white font-sans antialiased">
        <Navbar9 backPath="/projects" />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white font-sans antialiased">
        <Navbar9 backPath="/projects" />
        <div className="px-4 md:px-8 lg:px-16 py-12 max-w-4xl mx-auto">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <Building2 className="w-16 h-16 mx-auto mb-4" />
              <h2 className="text-2xl font-bold">Error Loading Shared Items</h2>
            </div>
            <p className="text-gray-600 mb-6">{error}</p>
            <button onClick={() => window.location.reload()} className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-sans antialiased">
      <Navbar9 backPath="/projects" />
      
      <div className="px-4 md:px-8 lg:px-16 py-12 max-w-7xl mx-auto">
        <div className="mb-8">
          <p className="text-gray-600">Content shared with you by other members of your enterprise.</p>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {/* Custom Search Bar */}
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by title or shared by..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-colors"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')} 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <XIcon className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Custom Sort Dropdown */}
          <div className="relative w-full md:w-56" ref={sortDropdownRef}>
            <button
              onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
              className="w-full flex items-center justify-between text-left pl-4 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent bg-white"
            >
              <span className="text-gray-700">{sortOptions[sortOption]}</span>
              <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isSortDropdownOpen ? 'transform rotate-180' : ''}`} />
            </button>
            {isSortDropdownOpen && (
              <div className="absolute top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                {Object.entries(sortOptions).map(([key, value]) => (
                  <button
                    key={key}
                    onClick={() => handleSortChange(key)}
                    className={`w-full text-left px-4 py-2 text-sm ${sortOption === key ? 'bg-gray-100 font-semibold' : 'hover:bg-gray-50'}`}
                  >
                    {value}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="relative min-h-[400px]">
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-black"></div>
            </div>
          )}
          
          {!loading && allItems.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {debouncedSearchTerm ? 'No Results Found' : 'No Shared Content Yet'}
              </h3>
              <p className="text-gray-500 mb-6">
                {debouncedSearchTerm 
                  ? 'No shared content matches your search criteria.'
                  : 'When colleagues share content with you, it will appear here.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {allItems.map((item) => {
                const expirationStatus = getExpirationStatus(item.expiresAt);
                const isExpired = expirationStatus?.status === 'expired';

                // 1. Using the new combined variable
                const isUnavailable = isExpired || item.isEnterpriseActive === false;

                return (
                  // 2. Using isUnavailable for styling and disabling elements
                  <div key={item.shareId} className={`border rounded-lg p-4 md:p-6 transition-shadow hover:shadow-md ${isUnavailable ? 'bg-gray-50' : 'bg-white'}`}>
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-lg font-semibold truncate pr-4 ${isUnavailable ? 'text-gray-500' : 'text-black'}`}>{item.itemTitle}</h3>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-500">
                          <div className="flex items-center"><User className="w-4 h-4 mr-1.5" />Shared by {item.sharedBy}</div>
                          <div className="flex items-center"><Calendar className="w-4 h-4 mr-1.5" />{formatDate(item.sharedAt)}</div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                          {item.hasPassword && <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full"><Lock className="w-3 h-3 mr-1" />Protected</span>}
                          {expirationStatus && <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${expirationStatus.status === 'expired' ? 'bg-red-100 text-red-800' : expirationStatus.status === 'expiring' ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-800'}`}><Clock className="w-3 h-3 mr-1" />{expirationStatus.text}</span>}
                          {/* 3. The new warning message for expired enterprise */}
                          {item.isEnterpriseActive === false && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                              <Clock className="w-3 h-3 mr-1" />
                              Unavailable: Enterprise has expired
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 w-full md:w-auto mt-4 md:mt-0 md:ml-4">
                        <button onClick={() => handleViewItem(item)} disabled={isUnavailable} className={`inline-flex items-center justify-center w-full md:w-28 px-3 py-2 text-sm font-medium rounded-md border ${isUnavailable ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} title={isUnavailable ? 'This content is unavailable' : 'View shared content'}>
                          <Eye className="w-4 h-4 mr-2" />View
                        </button>
                        <button onClick={() => handleCopyItem(item)} disabled={isUnavailable} className={`inline-flex items-center justify-center w-full md:w-28 px-3 py-2 text-sm font-medium rounded-md ${isUnavailable ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : item.hasCopied ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-black text-white hover:bg-gray-800'}`} title={isUnavailable ? 'This content is unavailable' : 'Copy to your projects'}>
                          {item.hasCopied ? <><CheckCircle className="w-4 h-4 mr-2" />Copied</> : <><Copy className="w-4 h-4 mr-2" />Copy</>}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-8 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, totalItems)}</span> of <span className="font-medium">{totalItems}</span> results
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button onClick={() => setCurrentPage(prev => prev - 1)} disabled={currentPage === 1 || loading} className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </button>
              <button onClick={() => setCurrentPage(prev => prev + 1)} disabled={currentPage === totalPages || loading} className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>

      {showCopyModal && selectedItem && ( <CopySharedModal item={selectedItem} onClose={() => { setShowCopyModal(false); setSelectedItem(null); }} onSuccess={handleCopySuccess}/> )}
    </div>
  );
};

export default SharedWithMe;